import {
  getMessageByServerIdFromLocalDB,
  getMessageCountByChatIdAndIdsFromLocalDB,
} from "@/indexedDB/services/messages";
import {
  JUMP_TO_MESSAGE_HIGHLIGHT_COLOR,
  JUMP_TO_MESSAGE_HIGHLIGHT_DURATION,
} from "@/config/constants";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { useNavigationState } from "@/zustand/NavigationState";
import Dexie from "dexie";

export const JUMP_TO_MESSAGE_HIGHLIGHT_ANIMATION = [
  { backgroundColor: "transparent" },
  { backgroundColor: JUMP_TO_MESSAGE_HIGHLIGHT_COLOR },
  { backgroundColor: JUMP_TO_MESSAGE_HIGHLIGHT_COLOR },
  { backgroundColor: "transparent" },
];

export const JUMP_TO_MESSAGE_HIGHLIGHT_ANIMATION_OPTIONS: KeyframeAnimationOptions =
  {
    duration: JUMP_TO_MESSAGE_HIGHLIGHT_DURATION,
    easing: "cubic-bezier(.83,1.74,.67,.86)",
    fill: "forwards",
  };

/**
 * Hook for handling message navigation and highlighting
 */
export function useMessageNavigation(chatId: string) {
  const { toast } = useToast();
  const { t } = useTranslation();

  const navigateToMessage = useCallback(
    async (parentMessageId: string, maxAttempts = 5) => {
      const setActiveChatMessageTake =
        useNavigationState.getState().setActiveChatMessageTake;

      // Helper function to highlight a message element
      const highlightElement = (el: Element) => {
        el.scrollIntoView({ behavior: "auto", block: "center" });

        // Use the Web Animations API for more control and better performance
        el.animate(
          JUMP_TO_MESSAGE_HIGHLIGHT_ANIMATION,
          JUMP_TO_MESSAGE_HIGHLIGHT_ANIMATION_OPTIONS,
        );
      };

      for (let i = 0; i < maxAttempts; i++) {
        // Try to find the message element in the current DOM
        const el = document.querySelector(
          `[data-message-id="${parentMessageId}"]`,
        );

        if (el) {
          // If found, scroll to it and highlight
          highlightElement(el);
          return;
        }

        // If not found in DOM, check if it exists in the database
        const msg = await getMessageByServerIdFromLocalDB(parentMessageId);

        if (!msg) {
          toast({
            duration: 2000,
            description: t("message.notReady"),
          });
          return;
        }

        // Calculate how many messages to load to include this message
        const startId = msg.id;
        // Use Dexie.maxKey as the upper bound to include all later messages
        // This avoids clock skew issues with generateUuid7() and ensures
        // the range always includes all messages after startId
        const endId = Dexie.maxKey;

        const offsetCount = await getMessageCountByChatIdAndIdsFromLocalDB(
          chatId,
          startId,
          endId,
        );

        // Set the new active chat message take with a buffer
        setActiveChatMessageTake(offsetCount + 10);

        // Add a delay for DB fetch/render to happen
        await new Promise((res) => setTimeout(res, 250));

        // Check if the message is now in the DOM
        const newEl = document.querySelector(
          `[data-message-id="${parentMessageId}"]`,
        );

        if (newEl) {
          highlightElement(newEl);
          return;
        }

        // If still not found, continue the loop
      }
    },
    [chatId, toast, t],
  );

  return { navigateToMessage };
}
